import React, { useState, Suspense, lazy } from 'react';
import DateAvisNia, { DatesAvisNia } from './DateAvisNia';
import { useNavigate } from 'react-router-dom';
import { Eye, LineChart, Wallet, Diamond, Bot, Star, ChevronDown, ChevronUp, Users } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import StockSearch from '../../components/Common/StockSearch';
import CompanyStoryCard from '../../components/Widgets/CompanyStory/CompanyStoryCard';
import { useAuth } from '../../context/AuthContext';
import './MobileTerminal.css';

type ActiveTab = 'overview' | 'orderbook' | 'analysis' | 'dividends' | 'valuation' | 'ai_summary' | 'peers';
type ChartPeriod = '1W' | '1M' | '3M' | '6M' | '1Y' | '5Y' | '10Y';

interface MobileTerminalProps {
    // Stock selection
    stocks: any[];
    selectedStock: string;
    setSelectedStock: (s: string) => void;
    stockDetails: any;
    isFavorited: boolean;
    toggleFavorite: () => void;

    // Tab state (shared with parent)
    activeTab: ActiveTab;
    setActiveTab: (t: any) => void;

    // Overview props
    quotations: any[];
    latestQuotation?: any;
    capitalisation: any;
    chartPeriod: ChartPeriod;
    setChartPeriod: (p: any) => void;
    calculatePerformance: (days: number) => { value: string; isPositive: boolean };
    getYTDPerformance: () => { value: string; isPositive: boolean };
    getWeeklyBreakdown: () => any[];
    formatLargeNumber: (num: number | null) => string;

    // Analysis props
    loading: boolean;
    chartData: any[];
    renderAnalysis: () => React.ReactNode;

    // Dividends
    renderDividends: () => React.ReactNode;

    // Valuation
    valuationData: any;
    renderValuation: () => React.ReactNode;
    renderPeers: () => React.ReactNode;

    // AI
    aiNarrative: string;
    aiNarrativeDates?: DatesAvisNia | null;
    aiLoading: boolean;
    aiSummaryType: 'VALUATION' | 'TECHNICAL';
    setAiSummaryType: (t: 'VALUATION' | 'TECHNICAL') => void;

    // Permissions
    hasPermission: (p: string) => boolean;
}

const TAB_CONFIG: { key: ActiveTab; icon: React.ReactNode; label: string; permission?: string }[] = [
    { key: 'overview', icon: <Eye size={16} />, label: 'Aperçu' },
    { key: 'analysis', icon: <LineChart size={16} />, label: 'Graphique' },
    { key: 'dividends', icon: <Wallet size={16} />, label: 'Dividendes' },
    { key: 'valuation', icon: <Diamond size={16} />, label: 'Indice Valeur', permission: 'view_advanced_data' },
    { key: 'peers', icon: <Users size={16} />, label: 'Pairs', permission: 'view_advanced_data' },
    { key: 'ai_summary', icon: <Bot size={16} />, label: 'Nia', permission: 'view_advanced_ai_data' },
];

const MobileTerminal: React.FC<MobileTerminalProps> = ({
    stocks,
    selectedStock,
    setSelectedStock,
    stockDetails,
    isFavorited,
    toggleFavorite,
    activeTab,
    setActiveTab,
    quotations,
    latestQuotation,
    capitalisation,
    chartPeriod,
    setChartPeriod,
    calculatePerformance,
    getYTDPerformance,
    getWeeklyBreakdown,
    formatLargeNumber,
    loading,
    chartData,
    renderAnalysis,
    renderDividends,
    valuationData,
    renderValuation,
    renderPeers,
    aiNarrative,
    aiNarrativeDates,
    aiLoading,
    aiSummaryType,
    setAiSummaryType,
    hasPermission,
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [detailsOpen, setDetailsOpen] = useState(false);

    const ALLOWED_DEMO_SYMBOLS = ['SNTS', 'ORAC', 'CIEC'];
    const isDemoStock = user?.role?.name === 'demo_user' && selectedStock && !ALLOWED_DEMO_SYMBOLS.includes(selectedStock.toUpperCase());

    const hasPermissionForStock = (permission: string): boolean => {
        const ADVANCED = ['view_advanced_data', 'view_advanced_ai_data'];
        if (isDemoStock && ADVANCED.includes(permission)) return false;
        return hasPermission(permission);
    };

    const effectiveLatestQuotation = latestQuotation || quotations[0];
    const price = effectiveLatestQuotation?.cours_cloture;
    const change = effectiveLatestQuotation?.variation_jour_pct;

    const handleTabClick = (tab: typeof TAB_CONFIG[number]) => {
        if (tab.permission && !hasPermissionForStock(tab.permission)) {
            navigate('/upgrade');
            return;
        }
        setActiveTab(tab.key);
    };

    // ------ OVERVIEW MOBILE ------
    const renderMobileOverview = () => {
        if (!stockDetails) {
            return <div className="mt-empty">Sélectionnez une société...</div>;
        }

        const weeklyBreakdown = getWeeklyBreakdown();

        const perfItems = [
            { label: 'Auj.', ...calculatePerformance(1), icon: '⏳' },
            { label: 'Sem.', ...calculatePerformance(5), icon: '📊' },
            { label: 'Mois', ...calculatePerformance(22), icon: '📈' },
            { label: 'YTD', ...getYTDPerformance(), icon: '🏆' },
        ];

        // Volume du jour
        const todayVolume = effectiveLatestQuotation?.volume_echange || effectiveLatestQuotation?.volume;

        return (
            <div className="mt-overview">
                {/* Fiche Pédagogique — L'Histoire en 2 min */}
                <CompanyStoryCard
                    symbole={stockDetails?.symbole}
                    nomSociete={stockDetails?.nom_societe}
                    secteur={stockDetails?.secteur}
                    style={{ marginBottom: '12px' }}
                />

                {/* Performance KPI Cards — 2x2 grid with modern borders */}
                <div className="mt-kpi-grid">
                    {perfItems.map((item, i) => {
                        const isNA = item.value === 'N/A';
                        return (
                            <div key={i} className={`mt-kpi-card ${isNA ? '' : item.isPositive ? 'kpi-positive' : 'kpi-negative'}`}>
                                <div className="mt-kpi-card-header">
                                    <span className="mt-kpi-card-icon">{item.icon}</span>
                                    <span className="mt-kpi-card-label">{item.label}</span>
                                </div>
                                <div className="mt-kpi-card-value">
                                    {!isNA && (
                                        <span className="mt-kpi-arrow">{item.isPositive ? '▲' : '▼'}</span>
                                    )}
                                    {item.value}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Volume du jour — pill */}
                {todayVolume != null && todayVolume > 0 && (
                    <div className="mt-volume-pill">
                        <span className="mt-volume-icon">📦</span>
                        <span className="mt-volume-label">Volume du jour</span>
                        <span className="mt-volume-value">{todayVolume.toLocaleString('fr-FR')}</span>
                    </div>
                )}

                {/* Weekly breakdown — enhanced cards */}
                {weeklyBreakdown.length > 0 && (
                    <div className="mt-weekly-section">
                        <h4 className="mt-section-title">
                            <span>📅 Derniers jours</span>
                        </h4>
                        <div className="mt-weekly-list">
                            {weeklyBreakdown.map((d: any, i: number) => (
                                <div key={i} className={`mt-weekly-card ${i === 0 ? 'today' : ''}`}>
                                    <div className="mt-weekly-card-left">
                                        <span className={`mt-weekly-dot ${d.change >= 0 ? 'dot-green' : 'dot-red'}`} />
                                        <div className="mt-weekly-card-date">
                                            <span className="mt-weekly-dayname">{d.day}</span>
                                            <span className="mt-weekly-fulldate">{d.date}</span>
                                        </div>
                                    </div>
                                    <div className="mt-weekly-card-right">
                                        <span className="mt-weekly-card-price">{d.price?.toLocaleString('fr-FR')} <small>FCFA</small></span>
                                        <span className={`mt-weekly-card-change ${d.change >= 0 ? 'positive' : 'negative'}`}>
                                            {d.change >= 0 ? '+' : ''}{d.change?.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Capitalisation - collapsible */}
                {capitalisation && (
                    <div className="mt-details-section">
                        <button className="mt-details-toggle" onClick={() => setDetailsOpen(!detailsOpen)}>
                            <span>📊 Détails Capitalisation</span>
                            {detailsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        {detailsOpen && (
                            <div className="mt-details-content">
                                <div className="mt-detail-row">
                                    <span>Capitalisation</span>
                                    <strong>{formatLargeNumber(capitalisation.capitalisation_boursiere || (capitalisation.quantite_totale_titres && effectiveLatestQuotation?.cours_cloture ? capitalisation.quantite_totale_titres * effectiveLatestQuotation.cours_cloture : null))}</strong>
                                </div>
                                <div className="mt-detail-row">
                                    <span>Cap. Flottante</span>
                                    <strong>{formatLargeNumber(capitalisation.capitalisation_flottante || (capitalisation.quantite_titres_flottant && effectiveLatestQuotation?.cours_cloture ? capitalisation.quantite_titres_flottant * effectiveLatestQuotation.cours_cloture : null))}</strong>
                                </div>
                                <div className="mt-detail-row">
                                    <span>Flottant</span>
                                    <strong>{capitalisation.flottant_pct ? `${capitalisation.flottant_pct.toFixed(1)}%` : '—'}</strong>
                                </div>
                                <div className="mt-detail-row">
                                    <span>Titres totaux</span>
                                    <strong>{capitalisation.quantite_totale_titres?.toLocaleString('fr-FR') || '—'}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Sector info pill */}
                {stockDetails.secteur && (
                    <div className="mt-sector-pill">
                        <span className="mt-sector-icon">🏭</span>
                        <span className="mt-sector-label">Secteur</span>
                        <span className="mt-sector-value">{stockDetails.secteur}</span>
                    </div>
                )}
            </div>
        );
    };

    // ------ AI SUMMARY MOBILE ------
    const renderMobileAI = () => (
        <div className="mt-ai">
            <div className="mt-ai-tabs">
                <button
                    className={`mt-ai-tab ${aiSummaryType === 'VALUATION' ? 'active fonda' : ''}`}
                    onClick={() => setAiSummaryType('VALUATION')}
                >
                    💼 Fondamentale
                </button>
                <button
                    className={`mt-ai-tab ${aiSummaryType === 'TECHNICAL' ? 'active tech' : ''}`}
                    onClick={() => setAiSummaryType('TECHNICAL')}
                >
                    📈 Technique
                </button>
            </div>
            <div className="mt-ai-content">
                {aiLoading ? (
                    <div className="mt-ai-loading">🧠 Analyse en cours...</div>
                ) : (
                    <>
                        {!aiNarrative.includes("❌") && !aiNarrative.includes("⏳") && <DateAvisNia dates={aiNarrativeDates ?? null} />}
                        <ReactMarkdown>{aiNarrative}</ReactMarkdown>
                        {!aiNarrative.includes("❌") && (
                            <div style={{ marginTop: '20px', padding: '15px', background: 'var(--bg-box)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                                ℹ️ Avertissement : Cet avis est généré par un algorithme d'intelligence artificielle à des fins éducatives. Il ne constitue pas un conseil personnalisé. L'investissement en bourse présente des risques de perte en capital.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className="mt-container">

            {/* Stock selector + fav */}
            <div className="mt-stock-bar">
                <div className="mt-stock-search">
                    <StockSearch
                        stocks={stocks}
                        value={selectedStock || ''}
                        onChange={(symbol) => setSelectedStock(symbol)}
                        placeholder="Rechercher..."
                    />
                </div>
                <button
                    className={`mt-fav-btn ${isFavorited ? 'active' : ''}`}
                    onClick={toggleFavorite}
                    aria-label={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                >
                    <Star size={20} fill={isFavorited ? '#fbbf24' : 'none'} color={isFavorited ? '#fbbf24' : '#94a3b8'} />
                </button>
            </div>

            {/* Hero: stock name + price */}
            {stockDetails && price && (
                <div className="mt-hero">
                    <div className="mt-hero-info">
                        <h2 className="mt-hero-symbol">{selectedStock}</h2>
                        <span className="mt-hero-name">{stockDetails.nom_societe}</span>
                    </div>
                    <div className="mt-hero-price-block">
                        <span className="mt-hero-price">{price?.toLocaleString('fr-FR')} FCFA</span>
                        <span className={`mt-hero-change ${(change ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                            {(change ?? 0) >= 0 ? '+' : ''}{(change ?? 0).toFixed(2)}%
                        </span>
                    </div>
                </div>
            )}

            {/* Scrollable Horizontal Tabs (Chips) */}
            {selectedStock && (
                <div className="mt-tabs-bar">
                    <div className="mt-tabs-scroll">
                        {TAB_CONFIG.map(tab => {
                            const isLocked = tab.permission && !hasPermissionForStock(tab.permission);
                            const isActive = activeTab === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`mt-tab-pill ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                                    onClick={() => handleTabClick(tab)}
                                >
                                    <span className="mt-tab-icon">{tab.icon}</span>
                                    <span className="mt-tab-label">{tab.label}</span>
                                    {isLocked && <span className="mt-tab-lock">🔒</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Content area based on active tab */}
            <div className="mt-content">
                {!selectedStock ? (
                    <div style={{ padding: '20px 12px' }}>
                        {/* Mobile Hero */}
                        <div style={{
                            textAlign: 'center', padding: '24px 16px',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08))',
                            borderRadius: '12px',
                            border: '1px solid rgba(102, 126, 234, 0.15)',
                            marginBottom: '20px',
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                            <p style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)', marginBottom: '4px' }}>
                                Bienvenue sur le Terminal
                            </p>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                Sélectionnez une société pour démarrer.
                            </p>
                        </div>
                        {/* Mobile Stock Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '8px',
                        }}>
                            {stocks.map(stock => (
                                <button
                                    key={stock.symbole}
                                    onClick={() => setSelectedStock(stock.symbole)}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '10px 4px',
                                        background: 'var(--bg-box)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        minHeight: '70px',
                                        gap: '6px',
                                    }}
                                >
                                    <img 
                                        src={`/images/societes/${stock.symbole}.png`} 
                                        alt={stock.symbole}
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '4px',
                                            objectFit: 'contain',
                                            background: 'white',
                                            padding: '1px',
                                        }}
                                    />
                                    <span style={{
                                        fontSize: '12px', fontWeight: '700',
                                        color: 'var(--text-main)',
                                    }}>
                                        {stock.symbole}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Demo banner for restricted stocks */}
                        {isDemoStock && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '8px 12px',
                                background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(124,58,237,0.08))',
                                border: '1px solid rgba(79,70,229,0.2)',
                                borderRadius: '8px', marginBottom: '12px',
                                fontSize: '12px', color: 'var(--text-muted)',
                            }}>
                                <span>🔒</span>
                                <span style={{ flex: 1 }}>
                                    <strong style={{ color: 'var(--text-main)' }}>Mode Démo</strong> — Valeur & Nia disponibles uniquement sur SNTS, ORAC, CIEC.
                                </span>
                            </div>
                        )}
                        {activeTab === 'overview' && renderMobileOverview()}
                        {activeTab === 'analysis' && (
                            <div className="mt-analysis-wrap">
                                {renderAnalysis()}
                            </div>
                        )}
                        {activeTab === 'dividends' && renderDividends()}
                        {activeTab === 'valuation' && (
                            hasPermissionForStock('view_advanced_data')
                              ? renderValuation()
                              : (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔒</div>
                                    <p style={{ fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '8px' }}>Fonctionnalité Pro</p>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                                        Disponible en démo sur SNTS, ORAC, CIEC uniquement.
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} style={{
                                        padding: '8px 20px', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                                        color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                                    }}>Voir les plans</button>
                                </div>
                              )
                        )}
                        {activeTab === 'peers' && (
                            hasPermissionForStock('view_advanced_data')
                              ? renderPeers()
                              : (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔒</div>
                                    <p style={{ fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '8px' }}>Fonctionnalité Pro</p>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                                        Disponible en démo sur SNTS, ORAC, CIEC uniquement.
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} style={{
                                        padding: '8px 20px', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                                        color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                                    }}>Voir les plans</button>
                                </div>
                              )
                        )}
                        {activeTab === 'ai_summary' && (
                            hasPermissionForStock('view_advanced_ai_data')
                              ? renderMobileAI()
                              : (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🤖</div>
                                    <p style={{ fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '8px' }}>Nia — Accès Pro</p>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                                        Disponible en démo sur SNTS, ORAC, CIEC uniquement.
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} style={{
                                        padding: '8px 20px', background: 'linear-gradient(135deg, #db2777, #9333ea)',
                                        color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                                    }}>Voir les plans</button>
                                </div>
                              )
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default MobileTerminal;
